import { Portal, useDisclosure, Popover } from '@chakra-ui/react';
import {
  CollaborationAccessEnum,
  CollaborationAccessLevelEnum,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { MdSettings } from 'react-icons/md';
import { useGetRecordShareEngagements } from '../../../api/shareService';
import { useExperienceConfiguration } from '../../../hooks';
import { MCustomIconButton } from '../../Monetize';
import { QuoteCollaborationExternal } from './QuoteCollaborationExternal';
import { QuoteCollaborationInternal } from './QuoteCollaborationInternal';

interface QuoteCollaborationPopoverV2Props {
  quoteId: string;
  quoteExpirationDate: string;
  quoteStatus: QuoteStatusEnum;
  collaborationAccess: CollaborationAccessEnum;
  accessLevel: CollaborationAccessLevelEnum | null;
  onCollaborationAccessChange: (
    collaborationAccess: CollaborationAccessEnum,
  ) => void;
}

export const QuoteCollaborationPopoverV2 = ({
  quoteId,
  quoteExpirationDate,
  collaborationAccess,
  accessLevel,
  quoteStatus,
  onCollaborationAccessChange,
}: QuoteCollaborationPopoverV2Props) => {
  const { quoteShareConfig } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { onOpen, onClose, open } = useDisclosure();

  const { data: engagements } = useGetRecordShareEngagements(quoteId, {
    enabled: quoteShareConfig?.enabled,
  });

  function handleOnOpen() {
    onOpen();
  }

  return (
    <Popover.Root
      positioning={{ placement: 'right-start' }}
      open={open}
      onOpenChange={(details) => {
        if (details.open) {
          handleOnOpen();
        } else {
          onClose();
        }
      }}
      restoreFocus={false}
      closeOnEscape
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          btnSize={4}
          minW={6}
          minH={6}
          variant="icon"
          icon={MdSettings}
          aria-label="Share settings"
          iconColor="tIndigo.base"
          onClick={() => {
            onOpen();
          }}
        />
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content minW="400px">
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <Popover.Header>Share Internally</Popover.Header>
            <Popover.Body>
              <QuoteCollaborationInternal
                quoteId={quoteId}
                quoteExpirationDate={quoteExpirationDate}
                quoteStatus={quoteStatus}
                collaborationAccess={collaborationAccess}
                accessLevel={accessLevel}
                onCollaborationAccessChange={onCollaborationAccessChange}
              />

              <QuoteCollaborationExternal
                quoteId={quoteId}
                quoteExpirationDate={quoteExpirationDate}
                quoteStatus={quoteStatus}
                accessLevel={accessLevel}
                engagements={engagements}
              />
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
